import { useMemo } from 'react';

interface FilterSummaryBarProps {
  theme: 'teal' | 'pink' | 'white';
  region: string;
  selectedDistricts: string[];
  maxPrice: number;
  minArea: number | '';
  selectedRooms: string[];
  selectedTypes: string[];
  petAllowed: boolean;
  moveInTime: string;
  notes: string;
  onClearRegion: () => void;
  onClearRooms: () => void;
  onClearTypes: () => void;
  onClearPet: () => void;
  onClearArea: () => void;
  onClearMoveIn: () => void;
}

interface ChipDef {
  id: string;
  icon: string;
  label: string;
  color: string;
  iconColor: string;
  onClear: () => void;
}

export default function FilterSummaryBar({
  theme,
  region,
  selectedDistricts,
  maxPrice,
  minArea,
  selectedRooms,
  selectedTypes,
  petAllowed,
  moveInTime,
  notes,
  onClearRegion,
  onClearRooms,
  onClearTypes,
  onClearPet,
  onClearArea,
  onClearMoveIn,
}: FilterSummaryBarProps) {

  const accentColor =
    theme === 'pink' ? 'rose' : theme === 'white' ? 'gray' : 'teal';

  const chips = useMemo<ChipDef[]>(() => {
    const list: ChipDef[] = [];

    if (region && region !== '全部地區') {
      const distLabel = selectedDistricts.length > 0
        ? `${region} · ${selectedDistricts.slice(0, 2).join('、')}${selectedDistricts.length > 2 ? ` +${selectedDistricts.length - 2}` : ''}`
        : region;
      list.push({
        id: 'region',
        icon: 'ri-map-pin-fill',
        label: distLabel,
        color: 'bg-violet-50 border-violet-200 text-violet-800',
        iconColor: 'text-violet-500',
        onClear: onClearRegion,
      });
    }

    list.push({
      id: 'price',
      icon: 'ri-money-dollar-circle-fill',
      label: `上限 NT$ ${maxPrice.toLocaleString()}`,
      color: 'bg-emerald-50 border-emerald-200 text-emerald-800',
      iconColor: 'text-emerald-500',
      onClear: () => {},
    });

    if (selectedRooms.length > 0) {
      list.push({
        id: 'rooms',
        icon: 'ri-door-fill',
        label: selectedRooms.join('、'),
        color: 'bg-amber-50 border-amber-200 text-amber-800',
        iconColor: 'text-amber-500',
        onClear: onClearRooms,
      });
    }

    if (selectedTypes.length > 0) {
      list.push({
        id: 'types',
        icon: 'ri-building-fill',
        label: selectedTypes.join('、'),
        color: 'bg-sky-50 border-sky-200 text-sky-800',
        iconColor: 'text-sky-500',
        onClear: onClearTypes,
      });
    }

    if (minArea !== '' && minArea > 0) {
      list.push({
        id: 'area',
        icon: 'ri-ruler-2-fill',
        label: `最少 ${minArea} 坪`,
        color: 'bg-orange-50 border-orange-200 text-orange-800',
        iconColor: 'text-orange-500',
        onClear: onClearArea,
      });
    }

    if (petAllowed) {
      list.push({
        id: 'pet',
        icon: 'ri-bear-smile-fill',
        label: '允許寵物',
        color: 'bg-pink-50 border-pink-200 text-pink-800',
        iconColor: 'text-pink-500',
        onClear: onClearPet,
      });
    }

    if (moveInTime) {
      const [y, m] = moveInTime.split('-');
      list.push({
        id: 'movein',
        icon: 'ri-calendar-fill',
        label: `${y} 年 ${m} 月入住`,
        color: 'bg-indigo-50 border-indigo-200 text-indigo-800',
        iconColor: 'text-indigo-500',
        onClear: onClearMoveIn,
      });
    }

    if (notes.trim()) {
      list.push({
        id: 'notes',
        icon: 'ri-chat-3-fill',
        label: '已補充說明',
        color: 'bg-slate-50 border-slate-200 text-slate-700',
        iconColor: 'text-slate-400',
        onClear: () => {},
      });
    }

    return list;
  }, [region, selectedDistricts, maxPrice, minArea, selectedRooms, selectedTypes, petAllowed, moveInTime, notes]);

  // 配對精準度計算
  const score = useMemo(() => {
    let s = 0;
    if (region && region !== '全部地區') s += 25;
    if (selectedDistricts.length > 0) s += 10;
    if (selectedRooms.length > 0) s += 20;
    if (selectedTypes.length > 0) s += 10;
    if (minArea !== '' && Number(minArea) > 0) s += 10;
    if (petAllowed) s += 5;
    if (moveInTime) s += 10;
    if (notes.trim().length > 10) s += 10;
    return Math.min(s, 100);
  }, [region, selectedDistricts, selectedRooms, selectedTypes, minArea, petAllowed, moveInTime, notes]);

  const scoreLabel =
    score >= 70 ? { text: '非常精準', color: 'text-emerald-600', barColor: 'bg-emerald-500' } :
    score >= 40 ? { text: '條件良好', color: 'text-amber-600', barColor: 'bg-amber-400' } :
                  { text: '條件不足', color: 'text-slate-400', barColor: 'bg-slate-300' };

  const meterGradient =
    theme === 'pink'
      ? 'from-rose-400 to-rose-500'
      : theme === 'white'
      ? 'from-gray-600 to-gray-800'
      : 'from-teal-400 to-teal-600';

  const headerBg =
    theme === 'pink'
      ? 'from-rose-500 to-rose-600'
      : theme === 'white'
      ? 'from-gray-700 to-gray-900'
      : 'from-teal-500 to-teal-700';

  return (
    <div className="rounded-2xl overflow-hidden border border-slate-200 mb-6">
      {/* 標題列 */}
      <div className={`bg-gradient-to-r ${headerBg} px-4 py-3 flex items-center justify-between`}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 flex items-center justify-center bg-white/20 rounded-lg">
            <i className="ri-filter-3-fill text-white text-sm"></i>
          </div>
          <div>
            <p className="text-white font-black text-sm">已設定篩選條件</p>
            <p className="text-white/60 text-[10px]">{chips.length} 個條件啟用中</p>
          </div>
        </div>

        {/* 配對精準度儀表 */}
        <div className="flex items-center gap-2.5 bg-white/10 rounded-xl px-3 py-2">
          <div className="text-right">
            <p className="text-white/60 text-[9px] font-medium">配對精準度</p>
            <p className="text-white font-black text-base leading-none tabular-nums">{score}%</p>
          </div>
          <div className="relative w-10 h-10 flex-shrink-0">
            <svg className="w-10 h-10 -rotate-90" viewBox="0 0 36 36">
              <circle cx="18" cy="18" r="15" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="3" />
              <circle
                cx="18" cy="18" r="15" fill="none"
                stroke="white" strokeWidth="3"
                strokeDasharray={`${(score / 100) * 94.2} 94.2`}
                strokeLinecap="round"
                style={{ transition: 'stroke-dasharray 0.5s ease' }}
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <i className={`ri-sparkling-2-fill text-white text-xs`}></i>
            </div>
          </div>
        </div>
      </div>

      {/* 精準度進度條 */}
      <div className="bg-slate-50 px-4 py-2.5 border-b border-slate-100">
        <div className="flex items-center gap-3">
          <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
            <div
              className={`h-full bg-gradient-to-r ${meterGradient} rounded-full transition-all duration-500`}
              style={{ width: `${score}%` }}
            />
          </div>
          <span className={`text-xs font-black ${scoreLabel.color} whitespace-nowrap`}>
            {scoreLabel.text}
          </span>
        </div>
        {score < 40 && (
          <p className="text-[10px] text-slate-400 mt-1">
            <i className="ri-information-line mr-0.5"></i>
            條件越多，AI 配對越精準，建議至少選擇地區與房型
          </p>
        )}
      </div>

      {/* 條件 Chips */}
      <div className="bg-white px-4 py-3">
        {chips.length === 0 ? (
          <div className="flex items-center gap-2 py-2">
            <i className="ri-list-check-3 text-slate-300 text-base"></i>
            <p className="text-xs text-slate-400">尚未設定任何條件，請從下方表單填寫</p>
          </div>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {chips.map((chip) => (
              <div
                key={chip.id}
                className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full border text-xs font-semibold ${chip.color} transition-all`}
              >
                <i className={`${chip.icon} ${chip.iconColor} text-xs flex-shrink-0`}></i>
                <span className="max-w-[120px] truncate">{chip.label}</span>
                {chip.onClear.toString() !== (() => {}).toString() && chip.id !== 'price' && chip.id !== 'notes' && (
                  <button
                    type="button"
                    onClick={chip.onClear}
                    className="ml-0.5 w-3.5 h-3.5 flex items-center justify-center rounded-full bg-black/10 hover:bg-black/20 cursor-pointer flex-shrink-0 transition-colors"
                  >
                    <i className="ri-close-line text-[9px]"></i>
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 底部提示 */}
      <div className="bg-slate-50 border-t border-slate-100 px-4 py-2 flex items-center gap-1.5">
        <i className="ri-robot-2-line text-slate-400 text-xs flex-shrink-0"></i>
        <p className="text-[10px] text-slate-400 leading-tight">
          填完聯絡資訊後送出，AI 將持續比對並在符合條件的房源上架時即時通知你
        </p>
      </div>
    </div>
  );
}
